import React from 'react';

import { Pencil, Trash2, Check, MessageSquare, Mail, Play, LogOut as LeaveIcon, RotateCcw, Package, Clock, Undo2, ClipboardCheck, Ban } from 'lucide-react';

import { formatPrice, daysSince, daysAgoLabel, toDate, STATUS_TEXT, isAwaitingReturn, isRunning, computeLaborSubtotal, computePartsSubtotal } from './lib/job-helpers.js';
import { vehicleLabel } from './Vehicles.jsx';

import { formatDocumentNumber } from './lib/job-number.js';

function JobBreakdown({ job }) {
  const labor = computeLaborSubtotal(job.lineItems);
  const parts = computePartsSubtotal(job.lineItems);
  const supplies = Number(job.shopSupplies) || 0;
  if (parts === 0 && supplies === 0) return null;
  const segments = [];
  if (labor > 0)    segments.push(`${formatPrice(labor)} labor`);
  if (parts > 0)    segments.push(`${formatPrice(parts)} parts`);
  if (supplies > 0) segments.push(`${formatPrice(supplies)} supplies`);
  return (
    <div className="job-breakdown muted small mono">
      {segments.join('  ·  ')}
    </div>
  );
}

function JobCard({ job, customer, vehicle, onSendEstimate, onSendInvoice, onMarkPaid, onStart, onLeave, onUndoStart, onEdit, onDelete, onInspect, onCancel }) {
  const vehicleText = vehicle ? vehicleLabel(vehicle) : (job.vehicleId ? '(deleted vehicle)' : '(no vehicle)');
  // '' for jobs created before numbering shipped — badge just hides.
  const docNumber = formatDocumentNumber(job);

  const hasEmail     = !!customer?.email;
  const prefersText  = customer?.preferredContact?.includes('text');
  const prefersEmail = customer?.preferredContact?.includes('email');

  // Decide which kind of doc the Send buttons should produce, what to
  // call them, and which timestamp powers the "X days ago" badge.
  let sendKind   = null;          // 'estimate' | 'invoice' | null
  let sendLabel  = null;          // tooltip + screen-reader text
  let extraActions = [];
  let ageTimestamp = null;
  let agePrefix    = null;

  if (job.status === 'paid') {
    // terminal — no send, no age badge (the "Paid" badge already says it)
  } else if (job.status === 'invoiced') {
    sendKind  = 'invoice';
    sendLabel = 'Resend invoice';
    extraActions.push({ label: 'Mark Paid', icon: Check, handler: onMarkPaid });
    agePrefix = 'Invoiced'; ageTimestamp = job.invoicedAt;
  } else if (job.status === 'approved') {
    sendKind  = 'invoice';
    sendLabel = 'Send invoice';
    extraActions.push({ label: 'Mark Paid', icon: Check, handler: onMarkPaid });
    agePrefix = 'Approved'; ageTimestamp = job.approvedAt;
  } else if (job.status === 'sent') {
    sendKind  = 'estimate';
    sendLabel = 'Resend estimate';
    agePrefix = 'Sent'; ageTimestamp = job.sentAt;
  } else {
    sendKind  = 'estimate';
    sendLabel = 'Send estimate';
  }

  const ageDays  = daysSince(ageTimestamp);
  const ageLabel = daysAgoLabel(ageDays, agePrefix);

  // Work-state chips and Start/Leave buttons. These are independent of
  // the financial status pill — a job can be "in progress" while still
  // status='approved', or "awaiting return" while status='invoiced'.
  const running          = isRunning(job);
  const awaitingRetVisit = isAwaitingReturn(job);
  const closedNotPaid    = !!job.closedAt && job.status !== 'paid';

  // The chip text under the status badge for return/part-waiting state.
  let pendingChip = null;
  if (awaitingRetVisit) {
    const next = nextReturnVisit(job);
    const when = next ? formatShortDate(toDate(next.scheduledAt)) : '';
    if (job.awaitingPart) {
      pendingChip = (
        <span className="age-pill pending-pill">
          <Package size={11} style={{ verticalAlign: '-2px', marginRight: 3 }} />
          Part arrives {when || 'soon'}
        </span>
      );
    } else {
      pendingChip = (
        <span className="age-pill pending-pill">
          <RotateCcw size={11} style={{ verticalAlign: '-2px', marginRight: 3 }} />
          Returning {when || 'soon'}
        </span>
      );
    }
  } else if (running) {
    const startMin = daysSince(job.startedAt) === 0
      ? Math.max(1, Math.round((Date.now() - toDate(job.startedAt).getTime()) / 60_000))
      : null;
    pendingChip = (
      <span className="age-pill running-pill">
        <Clock size={11} style={{ verticalAlign: '-2px', marginRight: 3 }} />
        {startMin ? `Started ${startMin} min ago` : 'In progress'}
      </span>
    );
  } else if (closedNotPaid) {
    const closedDays = daysSince(job.closedAt);
    pendingChip = (
      <span className="age-pill closed-pill">
        <Check size={11} style={{ verticalAlign: '-2px', marginRight: 3 }} />
        {daysAgoLabel(closedDays, 'Closed') || 'Closed'} — needs payment
      </span>
    );
  }

  function onSend(channel) {
    if (sendKind === 'invoice') onSendInvoice(channel);
    else onSendEstimate(channel);
  }

  return (
    <article className={`card ${running ? 'job-card-running' : ''}`}>
      <div className="card-body">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start', minWidth: 0 }}>
            <span className={`badge badge-${job.status || 'draft'}`}>{STATUS_TEXT[job.status] || 'Draft'}</span>
            {docNumber && <span className="doc-number mono">{docNumber}</span>}
            {ageLabel && <span className="age-pill">{ageLabel}</span>}
            {pendingChip}
          </div>
          <span className="mono" style={{ fontSize: 18, fontWeight: 700, color: 'var(--brand)' }}>
            {formatPrice(job.totalPrice)}
          </span>
        </div>
        <div className="card-title" style={{ fontSize: 15, fontWeight: 500 }}>
          {job.description}
        </div>
        <div className="card-sub" style={{ marginTop: 4 }}>{vehicleText}</div>
        <JobBreakdown job={job} />
      </div>
      <div className="card-actions">
        {/* Work tracker — Start / Leave. Hidden for paid (terminal) jobs. */}
        {job.status !== 'paid' && !running && (
          <button className="btn-link work-btn" onClick={onStart} title="Start working">
            <Play size={13} /> Start
          </button>
        )}
        {running && (
          <>
            <button className="btn-link work-btn leave" onClick={onLeave} title="Wrap up — close out or schedule a return">
              <LeaveIcon size={13} /> Leave
            </button>
            <button className="btn-link" onClick={onUndoStart} title="Undo Start (cancel the work timer)">
              <Undo2 size={13} /> Undo
            </button>
          </>
        )}
        {sendKind && (
          <>
            <button
              className={`btn-link ${prefersText ? 'preferred' : ''}`}
              onClick={() => onSend('text')}
              title={`${sendLabel} via text${prefersText ? ' (preferred)' : ''}`}
              aria-label={`${sendLabel} via text`}
            >
              <MessageSquare size={13} /> {sendKind === 'invoice'
                ? (job.status === 'invoiced' ? 'Resend Text' : 'Text Invoice')
                : (job.status === 'sent'     ? 'Resend Text' : 'Text Estimate')}
            </button>
            {hasEmail && (
              <button
                className={`btn-link ${prefersEmail ? 'preferred' : ''}`}
                onClick={() => onSend('email')}
                title={`${sendLabel} via email${prefersEmail ? ' (preferred)' : ''}`}
                aria-label={`${sendLabel} via email`}
              >
                <Mail size={13} /> {sendKind === 'invoice'
                  ? (job.status === 'invoiced' ? 'Resend Email' : 'Email Invoice')
                  : (job.status === 'sent'     ? 'Resend Email' : 'Email Estimate')}
              </button>
            )}
          </>
        )}
        {extraActions.map((a) => (
          <button key={a.label} className="btn-link" onClick={a.handler}>
            <a.icon size={13} /> {a.label}
          </button>
        ))}
        <button className="btn-link" onClick={onInspect} title="Vehicle inspection">
          <ClipboardCheck size={13} /> Inspect
        </button>
        <button
          className="btn-link"
          onClick={onCancel}
          title="Cancel this job — keeps its number, inspection and photos"
        >
          <Ban size={13} /> Cancel
        </button>
        <button className="btn-link" onClick={onEdit}>
          <Pencil size={13} /> Edit
        </button>
        <button className="btn-link danger" onClick={onDelete}>
          <Trash2 size={13} /> Delete
        </button>
      </div>
    </article>
  );
}

// Find the next-up return visit (earliest scheduledAt in the future, or
// just the earliest if all are past). Used for the chip label.
function nextReturnVisit(job) {
  if (!Array.isArray(job.returnVisits) || job.returnVisits.length === 0) return null;
  const now = Date.now();
  const sorted = [...job.returnVisits].sort((a, b) => {
    const aT = toDate(a.scheduledAt)?.getTime() ?? 0;
    const bT = toDate(b.scheduledAt)?.getTime() ?? 0;
    return aT - bT;
  });
  return sorted.find((v) => (toDate(v.scheduledAt)?.getTime() ?? 0) >= now) || sorted[sorted.length - 1];
}

function formatShortDate(d) {
  if (!d) return '';
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

// ─── Date/time helpers for the schedule-edit row in JobModal ─────
// Mirrors what Calendar.jsx's JobSidebar uses. If these get a third
// caller, they belong in lib/job-helpers.js.

export { JobCard, JobBreakdown };
